import React, { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import axios from 'axios'
import { BASEURL } from "../Config"
import moment from 'moment'
import Footer from "../components/Footer";
import Sidebar from "../components/Sidebar";
import Topbar from "../components/Topbar";
import swal from 'sweetalert';
import { State, City } from 'country-state-city';
import CustomPagination from "../components/common/CustomPagination";
import FilterModal from "../components/common/FilterModal";
import ActionBar from "../components/common/ActionBar";

const Distributors = () => {
    const history = useHistory();
    const location = useLocation();
    const [currentPage, setCurrentPage] = useState(0);
    const [offset, setOffset] = useState(0);
    const [slice, setSlice] = useState([]);
    const [perPage, setPerPage] = useState(5);
    const [pageCount, setPageCount] = useState(0);
    const [totalCount, setTotalCount] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [noResults, setNoResults] = useState(false);
    const [selectedStates, setSelectedStates] = useState([]);
    const [selectedCities, setSelectedCities] = useState([]);
    const [selectedSalesManager, setSelectedSalesManager] = useState(null);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [states, setStates] = useState([]);
    const [cities, setCities] = useState([]);
    const [allSalesManagers, setAllSalesManagers] = useState([]);
    const [showFilterModal, setShowFilterModal] = useState(false);
    const [filtersReady, setFiltersReady] = useState(false);

    useEffect(() => {
        const statesData = State.getStatesOfCountry('IN').map(state => ({
            label: state.name,
            value: state.isoCode
        }));
        setStates(statesData);

        // Parse URL parameters
        const params = new URLSearchParams(location.search);
        const createdBy = params.get('createdBy');
        const urlStartDate = params.get('startDate');
        const urlEndDate = params.get('endDate');

        if (urlStartDate) setStartDate(urlStartDate);
        if (urlEndDate) setEndDate(urlEndDate);

        // Fetch all sales managers for the dropdown
        axios.get(`${BASEURL}/api/v1/salesManager?offset=0&limit=1000&fields=firstName,lastName,uuid`)
            .then(res => {
                if (res.status === 200) {
                    const managersOptions = res.data.salesManagers.map(sm => ({
                        label: `${sm.firstName} ${sm.lastName}`,
                        value: sm.uuid
                    }));
                    setAllSalesManagers(managersOptions);

                    // Set sales manager from URL if present
                    if (createdBy) {
                        const managerFromUrl = managersOptions.find(m => m.value === createdBy);
                        if (managerFromUrl) {
                            setSelectedSalesManager(managerFromUrl);
                        }
                    }

                    setFiltersReady(true);
                }
            })
            .catch(err => console.log('Error fetching all sales managers', err));
    }, [location.search]);

    useEffect(() => {
        if (selectedStates.length > 0) {
            let allCities = [];
            selectedStates.forEach(state => {
                const citiesData = City.getCitiesOfState('IN', state.value).map(city => ({
                    label: city.name,
                    value: city.name
                }));
                allCities = [...allCities, ...citiesData];
            });
            setCities(allCities);
        } else {
            setCities([]);
            setSelectedCities([]);
        }
    }, [selectedStates]);

    useEffect(() => {
        if (filtersReady) {
            fetchDistributors();
        }
    }, [filtersReady, offset, perPage]);

    useEffect(() => {
        if (!filtersReady) return;
        const debounceTimer = setTimeout(() => {
            if (searchTerm !== undefined) {
                setOffset(0);
                setCurrentPage(0);
                fetchDistributors();
            }
        }, 500);

        return () => clearTimeout(debounceTimer);
    }, [searchTerm]);

    const fetchDistributors = () => {
        let url = `${BASEURL}/api/v1/distributor/fetchByQuery?offset=${offset}&limit=${perPage}&forAdmin=true`;
        if (searchTerm) {
            url += `&search=${searchTerm}`;
        }
        if (selectedStates.length > 0) {
            const stateNames = selectedStates.map(s => s.label).join(',');
            url += `&state=${stateNames}`;
        }
        if (selectedCities.length > 0) {
            const cityNames = selectedCities.map(c => c.label).join(',');
            url += `&cities=${cityNames}`;
        }
        if (selectedSalesManager) {
            url += `&createdBy=${selectedSalesManager.value}`;
        }
        if (startDate) {
            url += `&startDate=${startDate}`;
        }
        if (endDate) {
            url += `&endDate=${endDate}`;
        }

        axios.get(url)
            .then(res => {
                if (res.status === 200) {
                    const data = res.data.distributors || res.data;
                    const total = res.data.total || (Array.isArray(res.data) ? res.data.length : 0);
                    
                    setSlice(Array.isArray(data) ? data : []);
                    setPageCount(Math.ceil(total / perPage));
                    setTotalCount(total);
                    setNoResults((Array.isArray(data) ? data : []).length === 0);
                }
            })
            .catch((err) => {
                console.log(err?.response?.data)
                swal('Error in Fetching Distributors', err?.response?.data, 'error');
                console.log('Fetching Distributors Error', err);
            })
    };

    const handlePageClick = (e) => {
        const selectedPage = e.selected;
        const newOffset = selectedPage * perPage;
        setCurrentPage(selectedPage);
        setOffset(newOffset);
    };

    const handlePerPageChange = (e) => {
        const newPerPage = parseInt(e.target.value);
        setPerPage(newPerPage);
        setOffset(0);
        setCurrentPage(0);
    };

    const handleSearch = () => {
        setOffset(0);
        setCurrentPage(0);
        fetchDistributors();
    };

    const handleShowAll = () => {
        setSearchTerm('');
        setSelectedStates([]);
        setSelectedCities([]);
        setSelectedSalesManager(null);
        setStartDate('');
        setEndDate('');
        setNoResults(false);
        setCurrentPage(0);
        if (offset !== 0) {
            setOffset(0);
        } else {
             fetchDistributors();
        }
    };

    return (
        <div id="wrapper">
            <Sidebar act="distributors" />
            {/* Content Wrapper */}
            <div className="content-page">
                {/* Main Content */}
                <div className="content">
                    <Topbar act="Distributors" breadcrumbItems={['Sales App', 'Distributors']} />
                    {/* container */}
                    {/* content */}
                    <div className="sales-manager-container">
                        <ActionBar
                            addLink="addDistributor"
                            addLabel="Add Distributor"
                            searchTerm={searchTerm}
                            onSearchChange={(e) => setSearchTerm(e.target.value)}
                            showFilter={true}
                            onFilterClick={() => setShowFilterModal(!showFilterModal)}
                            placeholder="Search distributors..."
                            backButton={new URLSearchParams(location.search).get('createdBy') ? (
                                <button
                                    className="btn btn-sm btn-back-icon"
                                    onClick={() => {
                                        const params = new URLSearchParams(location.search);
                                        const createdBy = params.get('createdBy');
                                        const month = params.get('overviewMonth');
                                        const year = params.get('overviewYear');
                                        const date = params.get('selectedDate');
                                        let url = '/viewSalesManager/' + createdBy;
                                        const qs = new URLSearchParams();
                                        if (month) qs.set('overviewMonth', month);
                                        if (year) qs.set('overviewYear', year);
                                        if (date) qs.set('selectedDate', date);
                                        if (qs.toString()) url += '?' + qs.toString();
                                        history.push(url);
                                    }}
                                    title="Back to Sales Manager"
                                >
                                    <i className="mdi mdi-arrow-left"></i>
                                </button>
                            ) : null}
                        >
                            <FilterModal
                                show={showFilterModal}
                                onClose={() => setShowFilterModal(false)}
                                states={states}
                                cities={cities}
                                salesManagers={allSalesManagers}
                                selectedStates={selectedStates}
                                selectedCities={selectedCities}
                                selectedSalesManager={selectedSalesManager}
                                onStateChange={setSelectedStates}
                                onCityChange={setSelectedCities}
                                onSalesManagerChange={setSelectedSalesManager}
                                startDate={startDate}
                                endDate={endDate}
                                onStartDateChange={setStartDate}
                                onEndDateChange={setEndDate}
                                onApply={() => {
                                    handleSearch();
                                    setShowFilterModal(false);
                                }}
                                onReset={handleShowAll}
                            />
                        </ActionBar>

                        {(selectedSalesManager || selectedStates.length > 0 || selectedCities.length > 0 || startDate || endDate) && (
                            <div className="applied-filters">
                                <span className="applied-filters-label">Applied Filters:</span>
                                <div className="applied-filters-chips">
                                    {selectedSalesManager && (
                                        <span className="filter-chip">
                                            Sales Manager: {selectedSalesManager.label}
                                            <button className="filter-chip-remove" onClick={() => { setSelectedSalesManager(null); setTimeout(() => fetchDistributors(), 0); }}>
                                                <i className="mdi mdi-close"></i>
                                            </button>
                                        </span>
                                    )}
                                    {selectedStates.map(s => (
                                        <span key={s.value} className="filter-chip">
                                            State: {s.label}
                                            <button className="filter-chip-remove" onClick={() => { const updated = selectedStates.filter(st => st.value !== s.value); setSelectedStates(updated); setTimeout(() => fetchDistributors(), 0); }}>
                                                <i className="mdi mdi-close"></i>
                                            </button>
                                        </span>
                                    ))}
                                    {selectedCities.map(c => (
                                        <span key={c.value} className="filter-chip">
                                            City: {c.label}
                                            <button className="filter-chip-remove" onClick={() => { const updated = selectedCities.filter(ct => ct.value !== c.value); setSelectedCities(updated); setTimeout(() => fetchDistributors(), 0); }}>
                                                <i className="mdi mdi-close"></i>
                                            </button>
                                        </span>
                                    ))}
                                    {startDate && (
                                        <span className="filter-chip">
                                            From: {moment(startDate).format('DD MMM YYYY')}
                                            <button className="filter-chip-remove" onClick={() => { setStartDate(''); setTimeout(() => fetchDistributors(), 0); }}>
                                                <i className="mdi mdi-close"></i>
                                            </button>
                                        </span>
                                    )}
                                    {endDate && (
                                        <span className="filter-chip">
                                            To: {moment(endDate).format('DD MMM YYYY')}
                                            <button className="filter-chip-remove" onClick={() => { setEndDate(''); setTimeout(() => fetchDistributors(), 0); }}>
                                                <i className="mdi mdi-close"></i>
                                            </button>
                                        </span>
                                    )}
                                    <button className="filter-chip-clear-all" onClick={handleShowAll}>
                                        Clear All
                                    </button>
                                </div>
                            </div>
                        )}

                        <div className="table-card">
                            <div style={{ overflowX: 'auto' }}>
                                <table className="table mb-0">
                                    <thead>
                                        <tr>
                                            <th>SNo</th>
                                            <th>Name</th>
                                            <th>Phone Number</th>
                                            <th>State</th>
                                            <th>City</th>
                                            <th>Created By</th>
                                            <th>Created At</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    {noResults ?
                                        <tbody>
                                            <tr>
                                                <td colSpan={8} style={{ textAlign: 'center' }}>
                                                    No Results Found
                                                </td>
                                            </tr>
                                        </tbody>
                                        :
                                        <tbody>
                                            {slice.map((data, idx) => (
                                                <tr 
                                                    key={data.uuid} 
                                                    onClick={() => history.push('/viewDistributor/' + data.uuid)}
                                                    style={{ cursor: 'pointer' }}
                                                >
                                                    <td>{currentPage * perPage + idx + 1}</td>
                                                    <td>
                                                        <div className="user-name-cell">
                                                            <div className="user-avatar">
                                                                <i className="mdi mdi-account-group" style={{ fontSize: '20px' }}></i>
                                                            </div>
                                                            <span className="user-name-text">
                                                                {data.name}
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td>{data.phoneNo}</td>
                                                    <td>{data.state}</td>
                                                    <td>{data.city}</td>
                                                    <td>{data.createdByName}</td>
                                                    <td>{data.createdAt ? moment(data.createdAt).format('DD MMM YYYY') : '-'}</td>
                                                    <td>
                                                        <div className="action-buttons">
                                                            <button 
                                                                className="btn btn-sm btn-action"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    history.push('/editDistributor/' + data.uuid);
                                                                }}
                                                                title="Edit"
                                                            >
                                                                <i className="mdi mdi-pencil-outline"></i>
                                                            </button>
                                                            <button 
                                                                className="btn btn-sm btn-action"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    history.push('/viewDistributor/' + data.uuid);
                                                                }}
                                                                title="View"
                                                            >
                                                                <i className="mdi mdi-eye-outline"></i>
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    }
                                </table>
                            </div>

                            <CustomPagination
                                pageCount={pageCount}
                                onPageChange={handlePageClick}
                                currentPage={currentPage}
                                perPage={perPage}
                                onPerPageChange={handlePerPageChange}
                                offset={offset}
                                totalCount={totalCount}
                                sliceLength={slice.length}
                                itemLabel="distributors"
                            />
                        </div>
                    </div>
                    {/* End of Main Content */}
                    <Footer />
                </div>
                {/* End of Content Wrapper */}
            </div>
        </div>
    );
}

export default Distributors;
